'use client';

import Link from 'next/link';
import { usePathname } from 'next/navigation';
import { ArrowRight, ChevronDown } from 'lucide-react';

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger
} from '@workspace/ui/components/dropdown-menu';

import { isMarketingRouteActive } from '~/lib/marketing-navigation';
import {
  isResourceNavigationActive,
  resourceNavigationGroups,
  resourceUtilityLinks
} from '~/lib/resource-navigation';

export function ResourcesNavigation(): React.JSX.Element {
  const pathname = usePathname();
  const active = isResourceNavigationActive(pathname);

  return (
    <DropdownMenu>
      <DropdownMenuTrigger
        data-active={active || undefined}
        className="marketing-nav-link flex min-h-10 items-center gap-1.5 rounded-sm text-sm font-medium"
      >
        Resources
        <ChevronDown
          className="size-3.5"
          aria-hidden="true"
        />
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="start"
        sideOffset={14}
        className="w-[680px] max-w-[calc(100vw-32px)] rounded-xl p-3"
      >
        <DropdownMenuLabel className="px-3 pb-3 text-xs font-normal text-muted-foreground">
          Guides, workflows, and comparisons to put intelligence to work.
        </DropdownMenuLabel>
        <div className="grid grid-cols-3 gap-1">
          {resourceNavigationGroups.map((group) => (
            <DropdownMenuGroup
              key={group.label}
              aria-label={group.label}
            >
              <DropdownMenuLabel className="px-3 pb-2 pt-1 font-mono text-[10px] font-normal uppercase tracking-[0.1em] text-muted-foreground">
                {group.label}
              </DropdownMenuLabel>
              {group.links.map((link) => (
                <DropdownMenuItem
                  key={link.href}
                  asChild
                  className="marketing-nav-item min-h-11 rounded-lg px-3 py-2.5 text-[13px]"
                >
                  <Link
                    href={link.href}
                    aria-current={pathname === link.href ? 'page' : undefined}
                  >
                    {link.label}
                  </Link>
                </DropdownMenuItem>
              ))}
            </DropdownMenuGroup>
          ))}
        </div>
        <DropdownMenuSeparator className="my-2" />
        <div className="grid grid-cols-2 gap-1">
          {resourceUtilityLinks.map((link) => (
            <DropdownMenuItem
              key={link.href}
              asChild
              className="marketing-nav-item rounded-lg px-3 py-2.5"
            >
              <Link
                href={link.href}
                aria-current={
                  isMarketingRouteActive(pathname, link.href)
                    ? 'page'
                    : undefined
                }
                className="flex justify-between text-xs"
              >
                {link.label}
                <ArrowRight
                  className="size-3.5"
                  aria-hidden="true"
                />
              </Link>
            </DropdownMenuItem>
          ))}
        </div>
        <DropdownMenuSeparator className="my-2" />
        <DropdownMenuItem
          asChild
          className="marketing-nav-item rounded-lg px-3 py-2.5"
        >
          <Link
            href="/resources"
            aria-current={pathname === '/resources' ? 'page' : undefined}
            className="flex justify-between text-xs"
          >
            Explore all resources
            <ArrowRight
              className="size-3.5"
              aria-hidden="true"
            />
          </Link>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
